// @ts-strict-ignore
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { IsProjectType } from '@project/model/is-project-type';
import { Dictionary } from 'typescript-collections';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { ArticleTypeVO, ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ConnectionAreaVO, MetaElementVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleInfoVO, ProjectVO } from '@project/model/vo/project.vo';
import { NumberUtils } from '@shared/util/number-utils';
import { UserVO } from '@user/model/vo/user.vo';
import { ArticleTypeIds } from '../constants/article-type-ids';
import { IntRangeVO, PropertyVO } from '@project/components/article/model/vo/property.vo';
import { WireBridgeService } from '@project/components/article/manager/wire-bridge.service';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { DateFormatter } from '@shared/date-formatter';
import { RepresentationVO } from '@shared/vo/representation.vo';
import { ArticleConstants } from '../constants/article-constants';
import { ArticleTypeUtils } from '@article/util/article-type.utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticlePlausiPropertyDef } from '../constants/article-plausi-property-def';
import { MetaElementUtils } from '../components/connections/connection-creation/meta-element.utils';
import { ArticleUsageUtils } from './article-usage-utils';
import { TextureAreaUtils } from '@shared/util/texture-area-utils';
import { ArticleWithTechnicalPropertiesDTO } from '@article/properties/technical/technical-requirements';

export class ArticleUtils {
  private static readonly OUTDATED_STATUS_EOLA: number = 80;
  private static readonly OUTDATED_STATUS_EOS: number = 82;
  private static readonly OUTDATED_STATUS_EOP: number = 84;
  private static readonly OUTDATED_STATUS_EOD: number = 86;
  private static readonly OUTDATED_STATUS_EOSR: number = 88;
  private static readonly OUTDATED_STATUS_EOL: number = 90;

  private static readonly DISCONTINUED_STATUS = [
    ArticleUtils.OUTDATED_STATUS_EOS,
    ArticleUtils.OUTDATED_STATUS_EOP,
    ArticleUtils.OUTDATED_STATUS_EOD,
    ArticleUtils.OUTDATED_STATUS_EOSR,
    ArticleUtils.OUTDATED_STATUS_EOL,
  ];

  private static readonly OUTDATED_STATUS = [
    ArticleUtils.OUTDATED_STATUS_EOLA,
    ...ArticleUtils.DISCONTINUED_STATUS,
  ];

  private static readonly NOT_DELIVERED_STATUS = [
    ArticleUtils.OUTDATED_STATUS_EOD,
    ArticleUtils.OUTDATED_STATUS_EOSR,
    ArticleUtils.OUTDATED_STATUS_EOL,
  ];

  public static getArticleBehaviorPropertyStringValue(article: ArticleVO, propertyName: string): string {
    if (!article) {
      return null;
    }
    return PropertyUtils.getStringPropertyValue(article.behaviorProperties, propertyName);
  }

  public static getArticleBehaviorPropertyStringValues(article: ArticleVO, propertyName: string): string[] {
    if (!article) {
      return [];
    }
    return PropertyUtils.getStringPropertyValues(article.behaviorProperties, propertyName);
  }

  public static getArticleBehaviorPropertyBooleanValue(article: ArticleVO, propertyName: string): boolean {
    if (!article) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(article.behaviorProperties, propertyName);
  }

  public static getArticleBehaviorPropertyFloatValue(article: ArticleVO, propertyName: string): number {
    if (!article) {
      return NaN;
    }
    return PropertyUtils.getFloatPropertyValue(article.behaviorProperties, propertyName);
  }

  public static getArticleBehaviorPropertyIntValue(article: ArticleVO, propertyName: string): number {
    if (!article) {
      return NaN;
    }
    return PropertyUtils.getIntPropertyValue(article.behaviorProperties, propertyName);
  }

  public static getArticleBehaviorPropertyNumberValue(article: ArticleVO | undefined, propertyName: string): number {
    if (!article) {
      return NaN;
    }
    return PropertyUtils.getNumberPropertyValue(article.behaviorProperties, propertyName);
  }

  public static isMountingRail(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_MOUNTING_RAIL);
  }

  public static isDrillingHole(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_DRILLING_HOLE);
  }

  public static isCoverArticle(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfTypeId(article, ArticleTypeIds.COVER);
  }

  public static isArticleOfType(article: ArticleVO, type: string): boolean {
    if (!article || !article.type || !type) {
      return false;
    }
    return article.type.displayName === type;
  }

  public static isArticleTypeOfType(articleType: ArticleTypeVO, type: string): boolean {
    if (!articleType || !type) {
      return false;
    }
    return articleType.displayName === type;
  }

  public static isArticleOfTypeId(article: ArticleVO, typeId: number): boolean {
    if (!article || !article.type) {
      return false;
    }
    return article.type.id === typeId;
  }

  public static isArticleOfAnyTypeId(article: ArticleVO, typeIds: number[]): boolean {
    if (!article || !article.type || !typeIds) {
      return false;
    }
    return typeIds.indexOf(article.type.id) >= 0;
  }

  public static isDynamicArticle(article: ArticleVO): boolean {
    return PropertyUtils.getBooleanPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.DYNAMIC_LENGTH);
  }

  public static isDynamicLabelingArticle(article: ArticleVO): boolean {
    return this.isLabelingArticle(article) && this.isDynamicArticle(article);
  }

  public static isLabelingArticle(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return this.isLabelingArticleType(article.type);
  }

  public static isLabelingArticleType(articleType: ArticleTypeVO): boolean {
    return this.isArticleTypeOfType(articleType, ArticleTypeConstants.TYPE_LABELING);
  }

  public static findFirstConnectionArea(metaElements: MetaElementVO[], side: string = null): ConnectionAreaVO {
    for (const metaElement of metaElements) {
      if (metaElement instanceof ConnectionAreaVO && (!side || side === metaElement.connectionSide)) {
        return metaElement;
      }
    }
    return null;
  }

  public static replaceTargetArticlePropertiesBySourceArticle(
    targetArticleVO: ArticleVO,
    sourceArticleVO: ArticleVO,
    replaceConnectionProperties: boolean,
    replaceBehaviorProperties: boolean,
    replacePlausiProperties: boolean,
    isUndoOperation: boolean = false
  ) {
    if (replaceConnectionProperties) {
      MetaElementUtils.replacementPropertiesHandler(targetArticleVO.connectionRequirements, sourceArticleVO.connectionRequirements, isUndoOperation);
      MetaElementUtils.replacementPropertiesHandler(targetArticleVO.connectionFeatures, sourceArticleVO.connectionFeatures, isUndoOperation);
    }
    if (replaceBehaviorProperties) {
      MetaElementUtils.replacementPropertiesHandler(targetArticleVO.behaviorProperties, sourceArticleVO.behaviorProperties, isUndoOperation);
    }
    if (replacePlausiProperties) {
      MetaElementUtils.replacementPropertiesHandler(targetArticleVO.plausiProperties, sourceArticleVO.plausiProperties, isUndoOperation);
    }
  }

  public static findFirstTextureArea(metaElements: MetaElementVO[], textureTypes: string[] = null): TextureAreaVO {
    if (ArrayUtils.isEmpty(metaElements)) {
      return null;
    }
    for (const metaElement of metaElements) {
      if (metaElement instanceof TextureAreaVO && (!textureTypes || textureTypes.indexOf(metaElement.textureType) >= 0)) {
        return metaElement;
      }
    }
    return null;
  }

  public static getTextureElements(article: ArticleVO, types: string[] = null): TextureAreaVO[] {
    const result: TextureAreaVO[] = [];
    if (article) {
      for (const metaElement of article.metaElements) {
        if (metaElement instanceof TextureAreaVO && (!types || types.includes(metaElement.textureType))) {
          result.push(metaElement);
        }
      }
    }
    return result;
  }

  public static isWireBridgeArticle(article: ArticleVO): boolean {
    return (
      (this.isBridge(article) &&
        PropertyUtils.getBooleanPropertyValue(article.connectionFeatures, ArticleConnectionPropertyDef.WIRE_BRIDGE_ARTICLE)) ||
      this.isArticleOfType(article, ArticleTypeConstants.TYPE_WIRE_BRIDGE)
    );
  }

  public static isBridge(article: ArticleVO): boolean {
    return this.isArticleOfType(article, ArticleTypeConstants.TYPE_BRIDGE);
  }

  public static getConnectionElements(article: ArticleVO, side: string = null): ConnectionAreaVO[] {
    const result: ConnectionAreaVO[] = [];
    if (article) {
      for (const metaElement of article.metaElements) {
        if (metaElement instanceof ConnectionAreaVO && (!side || side === metaElement.connectionSide)) {
          result.push(metaElement);
        }
      }
    }
    return result;
  }

  public static getConnectionElementsRec(article: ArticleVO, side: string = null): ConnectionAreaVO[] {
    const result: ConnectionAreaVO[] = this.getConnectionElements(article, side);

    // Also get the connectionElements from base article, if present
    const baseArticle = article?.baseArticle;
    if (baseArticle && baseArticle !== article) {
      const baseElements = this.getConnectionElementsRec(baseArticle, side);
      if (baseElements && baseElements.length > 0) {
        result.push(...baseElements);
      }
    }
    return result;
  }

  public static findMetaElementByName(article: ArticleVO, metaElementName: string): MetaElementVO {
    if (!article || StringUtils.isEmpty(metaElementName)) {
      return null;
    }
    for (const metaElement of article.metaElements) {
      if (metaElement.name === metaElementName) {
        return metaElement;
      }
    }
    return null;
  }

  public static getTextTextureElements(article: ArticleVO): TextureAreaVO[] {
    return this.getTextureElements(article, TextureAreaVO.TEXT_TYPES);
  }

  public static getEditableTextureElements(article: ArticleVO): TextureAreaVO[] {
    if (!article) {
      return [];
    }
    const textureAreas = this.getTextureElements(article);
    const editableTextureAreas = textureAreas.filter((textureArea) => TextureAreaUtils.isEditable(textureArea));
    return editableTextureAreas;
  }

  public static getMetaElementsAsMap(article: ArticleVO): Dictionary<string, MetaElementVO> {
    return this.metaElementsToMap(article == null ? null : article.metaElements);
  }

  public static metaElementsToMap(metas: MetaElementVO[]): Dictionary<string, MetaElementVO> {
    const result: Dictionary<string, MetaElementVO> = new Dictionary<string, MetaElementVO>();
    if (metas) {
      for (const met of metas) {
        result.setValue(met.name, met);
      }
    }
    return result;
  }

  public static isTextTextureType(textureType: string): boolean {
    if (!textureType) {
      return false;
    }
    return TextureAreaVO.TEXT_TYPES.indexOf(textureType) >= 0;
  }

  public static isArticleReplaceable(article: ArticleVO, user: UserVO = null): boolean {
    if (!article) {
      return false;
    }
    return (
      this.isArticleDeletable(article) &&
      !PropertyUtils.getBooleanPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.IS_NOT_REPLACEABLE)
    );
  }

  public static isArticleMovable(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return !(this.isBlockingZone(article) || this.isDrillingHole(article));
  }

  public static isArticleSearchable(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return !PropertyUtils.getBooleanPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.NOT_SEARCHABLE);
  }

  public static isBlockingZone(article: ArticleVO): boolean {
    return this.isArticleOfType(article, ArticleTypeConstants.TYPE_BLOCKING_ZONE);
  }

  public static isCustomArticle(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_CUSTOM_ARTICLE);
  }

  public static findArticleInfoByGuid(guid: string, project: ProjectVO): ArticleInfoVO {
    if (!guid || !project || !project.dynamicArticlesInfo) {
      return null;
    }
    return this.findArticleInfoByGuidInSource(guid, project.dynamicArticlesInfo);
  }

  public static findArticleInfoByGuidInSource(guid: string, dynamicArticlesInfo: ArticleInfoVO[]): ArticleInfoVO {
    if (!guid || ArrayUtils.isEmpty(dynamicArticlesInfo)) {
      return null;
    }
    for (const articleInfo of dynamicArticlesInfo) {
      if (articleInfo.guid === guid) {
        return articleInfo;
      }
    }
    return null;
  }

  public static isTextBorderAvailable(article: ArticleVO): boolean {
    return !this.isLabelingArticle(article);
  }

  public static isDrawingLinesAvailable(article: ArticleVO): boolean {
    return ArticleUtils.getArticleBehaviorPropertyBooleanValue(article, ArticleBehaviorPropertyDef.DRAW_SEPARATION_LINES);
  }

  public static getArticleOverlapTypes(article: ArticleVO): string[] {
    return ArticleUtils.getArticleBehaviorPropertyStringValues(article, ArticleBehaviorPropertyDef.OVERLAP_TYPE);
  }

  public static getArticleOverlapLeft(article: ArticleVO): number {
    return ArticleUtils.getArticleBehaviorPropertyNumberValue(article, ArticleBehaviorPropertyDef.OVERLAP_LEFT);
  }

  public static getArticleOverlapRight(article: ArticleVO): number {
    return ArticleUtils.getArticleBehaviorPropertyNumberValue(article, ArticleBehaviorPropertyDef.OVERLAP_RIGHT);
  }

  public static isArticleNumberHidden(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.IS_ARTICLE_NUMBER_HIDDEN);
  }

  public static isArticleDeletable(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return !this.isBaseArticle(article);
  }

  public static isBaseArticle(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    const baseArticleTypes: string[] = ArticleTypeConstants.BASE_ARTICLE_TYPES;
    if (!baseArticleTypes || baseArticleTypes.length === 0) {
      return false;
    }
    for (const baseArticleType of baseArticleTypes) {
      if (this.isArticleOfType(article, baseArticleType)) {
        return true;
      }
    }
    return false;
  }

  public static getArticleBehaviorPropertyIntRangeValue(article: ArticleVO, propertyName: string): IntRangeVO {
    if (!article) {
      return null;
    }
    return PropertyUtils.getIntRangePropertyValue(article.behaviorProperties, propertyName);
  }

  public static isCompositeArticle(article: ArticleVO): boolean {
    if (!article || !article.representations) {
      return false;
    }
    for (const rep of article.representations) {
      if (rep.type === RepresentationVO.TYPE_PROJECT) {
        return true;
      }
    }
    return false;
  }

  public static getImageUsageTextureElements(article: ArticleVO): TextureAreaVO[] {
    return this.getTextureElements(article, TextureAreaVO.IMAGE_USAGE_TYPES);
  }

  public static getBooleanArticlePlausiProperty(article: ArticleVO, propertyName: string): boolean {
    if (!article) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(article.plausiProperties, propertyName);
  }

  public static getConnectionAreasByValidBooleanPlausiPropertyName(article: ArticleVO, plausiPropertyName: string): MetaElementVO[] {
    return this.getConnectionAreasByPlausiPropertyNamesAndValue(article, [plausiPropertyName], true);
  }

  private static getConnectionAreasByPlausiPropertyNamesAndValue(
    article: ArticleVO,
    plausiPropertyNames: string[],
    value: boolean = false
  ): MetaElementVO[] {
    if (!article || ArrayUtils.isEmpty(article.metaElements)) {
      return [];
    }
    if (ArrayUtils.isEmpty(plausiPropertyNames)) {
      return article.metaElements;
    }
    const result: MetaElementVO[] = [];
    for (const metaElement of article.metaElements) {
      if (!metaElement || !(metaElement instanceof ConnectionAreaVO)) {
        continue;
      }
      const connectionArea: ConnectionAreaVO = metaElement;
      if (ArrayUtils.isEmpty(connectionArea.connectionPlausiProperties)) {
        continue;
      }
      if (!value) {
        if (PropertyUtils.sourcePropertiesContainTargetPropertyNames(connectionArea.connectionPlausiProperties, plausiPropertyNames)) {
          result.push(connectionArea);
        }
      } else {
        let isValidResult: boolean = false;
        for (const connectionPlausiProperty of connectionArea.connectionPlausiProperties) {
          if (!connectionPlausiProperty || plausiPropertyNames.indexOf(connectionPlausiProperty.name) === -1) {
            continue;
          }
          isValidResult = PropertyUtils.propertyContainsValueAsPropertyValue(connectionPlausiProperty, value);
          if (isValidResult) {
            break;
          }
        }
        if (isValidResult) {
          result.push(connectionArea);
        }
      }
    }
    return result;
  }

  public static isArticleWithout3DRepresentation(article: ArticleVO): boolean {
    return !this.getArticleRepresentationByType(article, RepresentationVO.TYPE_VIEW3D);
  }

  public static getMountingRailType(article: ArticleVO): string {
    return ArticleUtils.getArticleBehaviorPropertyStringValue(article, ArticleBehaviorPropertyDef.MOUNTING_RAIL_TYPE);
  }

  public static getArticleRepresentationByType(article: ArticleVO, representationType: string): RepresentationVO {
    if (!article || !article.representations) {
      return null;
    }
    for (const rep of article.representations) {
      if (rep.type === representationType) {
        return rep;
      }
    }
    return null;
  }

  public static isFreeMounting(article: ArticleVO): boolean {
    if (!article || !article.type) {
      return false;
    }
    return article.identity === ArticleConstants.DEFAULT_FREE_MOUNTING_ARTICLE_ID;
  }

  public static isEndPlateArticle(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_END_PLATE) ||
      ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_MCS_GDS_END_PLATE);
  }

  public static isEndPlateArticleType(articleType: string): boolean {
    if (!articleType) {
      return false;
    }
    return ArticleTypeConstants.TYPE_END_PLATE === articleType || ArticleTypeConstants.TYPE_MCS_GDS_END_PLATE === articleType;
  }

  public static isEndStopArticle(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_END_STOP);
  }

  public static isTerminalBlockArticle(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfTypeId(article, ArticleTypeIds.TERMINAL_BLOCK);
  }

  public static addLeadingZerosInArticleNumber(articleNo: string): string {
    return articleNo ? articleNo.replace(/(\d+)/g, match => match.padStart(4, '0')): "";
  }

  public static getArticleSeries(articleNo: string): string {
    return articleNo.substring(0, articleNo.indexOf('-'));
  }

  public static isCoverLabelStripArticle(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return ArticleUtils.getArticleBehaviorPropertyBooleanValue(article, ArticleBehaviorPropertyDef.IS_COVER_LABEL_STRIP);
  }

  public static isArticleConfigured(article: ArticleVO): boolean {
    return !ArticleUtils.isBlockingZone(article);
  }

  public static isArticleOutdated(article: ArticleVO | ArticleWithTechnicalPropertiesDTO): boolean {
    if (article) {
      return this.isStatusOutdated(article.materialStatus);
    }
    return false;
  }

  public static isStatusDiscontinued(materialStatus: number): boolean {
    if (NumberUtils.isNumber(materialStatus) && ArticleUtils.DISCONTINUED_STATUS.includes(materialStatus)) {
      return true;
    }
    return false;
  }

  public static isStatusOutdated(materialStatus: number): boolean {
    if (NumberUtils.isNumber(materialStatus) && ArticleUtils.OUTDATED_STATUS.includes(materialStatus)) {
      return true;
    }
    return false;
  }

  public static isStatusNotDelivered(materialStatus: number): boolean {
    if (NumberUtils.isNumber(materialStatus) && ArticleUtils.NOT_DELIVERED_STATUS.includes(materialStatus)) {
      return true;
    }
    return false;
  }

  public static getOutdatedArticleLabelWithContinue(article: ArticleVO, localeResourceManager: LocaleResourceManager): string {
    const labelKey: string = 'outdatedArticles.popup.' + article.materialStatus;
    const outdatedText = this.getOutdatedText(localeResourceManager, labelKey, article.materialStatus, article.articleNo, article.lastCall);
    const useAnywayText = localeResourceManager.getValueByKey('outdatedArticles.popup.use_anyway');
    return `${outdatedText} ${useAnywayText}`;
  }

  public static getOutdatedArticleLabel(localeResourceManager: LocaleResourceManager, article: ArticleVO | ArticleWithTechnicalPropertiesDTO): string {
    const labelKey: string = 'outdatedArticles.popup.' + article.materialStatus;
    const label = this.getOutdatedText(localeResourceManager, labelKey, article.materialStatus, '', article.lastCall);
    return label;
  }

  public static getOutdatedConfigurationText(
    localeResourceManager: LocaleResourceManager,
    configurationName: string,
    materialStatus: number,
    lastCall: Date
  ) {
    const labelKey: string = 'outdatedConfiguration.popup.' + materialStatus;
    const label = this.getOutdatedText(localeResourceManager, labelKey, materialStatus, configurationName, lastCall);
    return label;
  }

  private static getOutdatedText(
    localeResourceManager: LocaleResourceManager,
    labelKey: string,
    materialStatus: number,
    articleNo: string,
    lastCall: Date
  ) {
    if (!this.isStatusOutdated(materialStatus)) {
      return '';
    }

    let dateString;

    if (materialStatus === this.OUTDATED_STATUS_EOLA && lastCall !== null && lastCall >= new Date()) {
      labelKey = labelKey + '.withdate';
      dateString = DateFormatter.formatShortDate(lastCall);
    }

    const label = localeResourceManager.getValueByKey(labelKey, [articleNo, dateString]);
    return label;
  }

  public static canAddArticleToFreeMounting(project: ProjectVO, user: UserVO, article: ArticleVO): boolean {
    if (ArticleUtils.isLtlArticle(article)) {
      if (user.isAdmin()) {
        return true;
      }
    } else {
      if (user.isAdmin() || user.isClerk()) {
        return true;
      }
    }

    if (!IsProjectType.freeMounting(project) ) {
      return true;
    }

    const articleType = article?.type?.id;
    return !([
      ArticleTypeIds.MULTI_CONNECTORS,
      ArticleTypeIds.PCB_TERMINAL_BLOCKS,
      ArticleTypeIds.MCS_GDS_END_PLATE,
      ArticleTypeIds.LTL,
    ] as number[]).includes(articleType);
  }

  public static getWireBridgeMultiplyRange(article: ArticleVO): IntRangeVO {
    let intRange: IntRangeVO = ArticleUtils.getArticleBehaviorPropertyIntRangeValue(article, ArticleBehaviorPropertyDef.MULTIPLY_RANGE);
    if (!intRange) {
      intRange = new IntRangeVO();
      intRange.left = WireBridgeService.DEFAULT_LEFT_EDGE_RANGE;
      intRange.right = WireBridgeService.DEFAULT_RIGHT_EDGE_RANGE;
    }
    return intRange;
  }

  public static getKeyByIdAndVersion(articleId: number, version: number): string {
    return articleId + '_' + version;
  }

  public static isBusbarOrCoverArticle(article: ArticleVO): boolean {
    return this.isBusbarArticle(article) || this.isCoverArticle(article);
  }

  public static isBusbarArticle(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_N_BUSBAR);
  }

  public static isAdapterArticle(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_MARKER_CARRIER);
  }

  public static isStaggeredJumperArticle(article: ArticleVO): boolean {
    return ArticleUtils.getArticleBehaviorPropertyBooleanValue(article, ArticleBehaviorPropertyDef.IS_STAGGERED_JUMPER);
  }

  public static isEmptyZoneArticle(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_EMPTY_ZONE);
  }

  public static isZoneArticle(article: ArticleVO): boolean {
    return ArticleUtils.isBlockingZone(article) || ArticleUtils.isEmptyZoneArticle(article);
  }

  public static isExcludedFromPrintLabeling(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.IS_EXCLUDED_FROM_PRINT_LABELING);
  }

  public static isMcsProjectArticleType(type: number): boolean {
    return ArticleTypeUtils.getAvailableArticleTypeIds('MCS').includes(type);
  }

  public static isMcsProjectArticle(article: ArticleVO): boolean {
    return ArticleTypeUtils.getAvailableArticleTypeIds('MCS').includes(article?.type?.id);
  }

  public static isLtlArticle(article: ArticleVO): boolean {
    return article.series === '274';
  }

  public static isMcsGdsProjectArticle(article: ArticleVO): boolean {
    return this.isMcsProjectArticle(article) || this.isGdsProjectArticle(article);
  }

  public static isMcsGdsLtlProjectArticle(article: ArticleVO): boolean {
    return this.isMcsProjectArticle(article) || this.isGdsProjectArticle(article) || this.isLtlArticle(article);
  }

  public static isGdsProjectArticleType(type: number): boolean {
    if (!type) {
      return false;
    }
    const availableArticleTypeIds: number[] = ArticleTypeUtils.getAvailableArticleTypeIds("GDS");
    return availableArticleTypeIds.indexOf(type) !== -1;
  }

  public static isGdsProjectArticle(article: ArticleVO): boolean {
    if (!article || !article.type) {
      return false;
    }
    const availableArticleTypeIds: number[] = ArticleTypeUtils.getAvailableArticleTypeIds("GDS");
    return availableArticleTypeIds.indexOf(article.type.id) !== -1;
  }

  public static isArticleCanBeSavedAsBuildingBlock(article: ArticleVO): boolean {
    if (
      ArticleUtils.isMountingRail(article) ||
      ArticleUtils.isDrillingHole(article) ||
      ArticleUtils.isCustomArticle(article) ||
      this.isGdsProjectArticle(article) ||
      this.isMcsProjectArticle(article)
    ) {
      return false;
    } else {
      return true;
    }
  }

  public static getArticleAlternativeGroupProperty(article: ArticleVO): PropertyVO {
    if (!article) {
      return null;
    }
    const alternativGroup: PropertyVO = PropertyUtils.getPropertyByName(
      article.connectionFeatures,
      ArticleConnectionPropertyDef.ARTICLE_ALTERNATIVE_GROUP
    );
    return alternativGroup;
  }

  public static getArticleAlternativeGroups(article: ArticleVO, validateConnectionFeatures: boolean): string[] {
    if (!article) {
      return [];
    }
    const alternativGroups: string[] = PropertyUtils.getStringPropertyValues(
      article.connectionFeatures,
      ArticleConnectionPropertyDef.ARTICLE_ALTERNATIVE_GROUP
    );
    if (validateConnectionFeatures) {
      for (const element of article.metaElements) {
        if (element instanceof ConnectionAreaVO) {
          alternativGroups.concat(
            PropertyUtils.getStringPropertyValues(
              (element as ConnectionAreaVO).connectionFeatures,
              ArticleConnectionPropertyDef.ARTICLE_ALTERNATIVE_GROUP
            )
          );
        }
      }
    }
    return alternativGroups;
  }

  public static isCodingFinger(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfAnyTypeId(article, [ArticleTypeIds.CODING_FINGER, ArticleTypeIds.SNAPIN_CODING_FINGER]);
  }

  public static isContact(article: ArticleVO): boolean {
    return ArticleUtils.isArticleOfType(article, ArticleTypeConstants.TYPE_CONTACT);
  }



  public static isSpeedway(article: ArticleVO): boolean {
    if (!article || !article.type) {
      return false;
    }
    return article.identity === ArticleConstants.DEFAULT_SPEEDWAY_ARTICLE_ID;
  }

  public static isAddingToCollectionAvailable(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return !this.isCustomArticle(article);
  }

  public static getGivingXcomConnectionAreas(article: ArticleVO): ConnectionAreaVO[] {
    if (!article) {
      return [];
    }
    const connectionElements: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(article, ConnectionAreaVO.SIDE_GIVING);
    const connectionAreas: ConnectionAreaVO[] = [];
    for (const connectionArea of connectionElements) {
      const typProperty: PropertyVO = PropertyUtils.getPropertyByName(connectionArea.connectionFeatures, ArticleConnectionPropertyDef.TYPE);
      if (PropertyUtils.containsStringPropertyValue(typProperty, ArticleConnectionPropertyValue.XCOM_TYPE)) {
        connectionAreas.push(connectionArea);
      }
    }
    return connectionAreas;
  }

  public static isIO_750_753Article(article: ArticleVO): boolean {
    return ArticleUtils.getBooleanArticlePlausiProperty(article, ArticlePlausiPropertyDef.IS_IO_750_753);
  }

  public static containsDefaultWagoArticleNo(articleNo: String): boolean {
    return articleNo.match('(([0-9]{3,4})-([0-9]{3,4})(/?))+') != null;
  }

  public static containsInternalArticle(articles: ArticleContainer[]): boolean {
    let hasInternalArticle = false;
    articles.forEach((article) => {
      if (this.isInternalArticle(article)) {
        hasInternalArticle = true;
      }
    });
    return hasInternalArticle;
  }

  public static isInternalArticle(article: ArticleContainer): boolean {
    if (!article) {
      return true;
    }
    return this.isInternalArticleVO(article.article);
  }

  public static isInternalArticleVO(article: ArticleVO): boolean {
    if (!article) {
      return true;
    }
    return PropertyUtils.getBooleanPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.IS_INTERNAL);
  }

  public static isMountingFootArticle(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return PropertyUtils.getBooleanPropertyValue(article.plausiProperties, ArticlePlausiPropertyDef.MOUNTING_FOOT);
  }

  public static getArticleMountingType(article: ArticleVO): string {
    if (!article) {
      return null;
    }
    return PropertyUtils.getStringPropertyValue(article.plausiProperties, ArticlePlausiPropertyDef.MOUNTING_TYPE);
  }

  public static isLockingClipMountingType(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    const propertyValues: string[] = PropertyUtils.getStringPropertyValues(article.plausiProperties, ArticlePlausiPropertyDef.MOUNTING_TYPE);
    return propertyValues.includes(ArticlePlausiPropertyDef.MOUNTING_TYPE_VALUE_SNAP_FOOT);
  }

  public static isArticlePEKlemme(article: ArticleVO): boolean {
    return ArticleUtils.getBooleanArticlePlausiProperty(article, ArticlePlausiPropertyDef.IST_PE_KLEMME);
  }

  public static isArticleNKlemme(article: ArticleVO): boolean {
    return ArticleUtils.getBooleanArticlePlausiProperty(article, ArticlePlausiPropertyDef.IST_N_KLEMME);
  }

  public static getLabelingCategory(article: ArticleVO): string {
    const category: string = ArticleUtils.getArticleBehaviorPropertyStringValue(article, ArticleBehaviorPropertyDef.LABELING_CATEGORY);
    return category ? category : '';
  }

  public static isMarkerCarrierWithSeparateLabeling(article: ArticleVO): boolean {
    return ArticleUtils.getArticleBehaviorPropertyBooleanValue(article, ArticleBehaviorPropertyDef.IS_MARKER_CARRIER_WITH_SEPARATE_LABELING);
  }

  public static isLabelingAcrossOverlapAllowed(article: ArticleVO): boolean {
    return ArticleUtils.getArticleBehaviorPropertyBooleanValue(article, ArticleBehaviorPropertyDef.IS_LABELING_ACROSS_OVERLAP_ALLOWED);
  }

  public static getGivingConnectionsInActiveSet(article: ArticleVO, usage: ArticleUsageVO): ConnectionAreaVO[] {
    if (!article || !usage) {
      return null;
    }
    const activeSetId: string = ArticleUsageUtils.getActiveConnectionSetId(usage);
    return this.getGivingConnectionsSetFromArticle(article, activeSetId);
  }

  public static getGivingConnectionsSetFromArticle(article: ArticleVO, connectionSetId: string): ConnectionAreaVO[] {
    if (!article) {
      return [];
    }
    const givingAreas: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(article, ConnectionAreaVO.SIDE_GIVING);
    const metaElementsInSet: ConnectionAreaVO[] = [];
    let currentConnectionSetId: string;
    for (const metaElement of givingAreas) {
      currentConnectionSetId = MetaElementUtils.getConnectionSetId(metaElement);
      if (connectionSetId === currentConnectionSetId) {
        metaElementsInSet.push(metaElement);
      }
    }
    return metaElementsInSet;
  }

  public static connectionAreas(article: ArticleVO): ConnectionAreaVO[] {
    return article.metaElements.filter(metaElement => metaElement instanceof ConnectionAreaVO);
  }


  public static separatedDisplayName(displayName: string): [string, string] {
    if(!displayName) return ["", ""];
    if(!displayName.includes(";")) return [displayName, ""];
    return displayName.split(/;(.+)/).splice(0, 2) as [string, string];
  }

  public static givingAreas(article: ArticleVO) {
    return this.connectionAreas(article).filter((connectionArea) => connectionArea.connectionSide === ConnectionAreaVO.SIDE_GIVING);
  }

  public static isStringArticleId(name: string): boolean {
    return !name.search(new RegExp(/^\d+$/)) && +name <= Number.MAX_SAFE_INTEGER;
  }
}
